import React, { useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import api, { formatApiError } from "../../lib/api";
import { Target, Save, RefreshCw, TrendingUp } from "lucide-react";

/**
 * Monthly targets per team-user + live achievement view.
 *
 * The picker on top narrows the period. Left column lists team users
 * (from /admin/debt/team-users). Right column shows target form + a
 * live achievement snapshot fetched from
 * /admin/targets/{user_id}/achievement.
 */
const inrFmt = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);
const currentPeriod = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

export default function EmployeeTargets() {
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [period, setPeriod] = useState(currentPeriod());
  const [targets, setTargets] = useState([]);     // for period
  const [form, setForm] = useState({});           // editing form
  const [ach, setAch] = useState(null);           // achievement for selected
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [u, t] = await Promise.all([
        api.get("/admin/debt/team-users"),
        api.get("/admin/targets", { params: { period } }),
      ]);
      setUsers(u.data.items || []);
      setTargets(t.data.items || []);
      if (!selectedId && (u.data.items || [])[0]) {
        setSelectedId(u.data.items[0].user_id);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [period]);

  const loadAch = async () => {
    if (!selectedId) return;
    try {
      const { data } = await api.get(`/admin/targets/${selectedId}/achievement`, { params: { period } });
      setAch(data);
      // hydrate form with existing target values (if any)
      const t = data.target || {};
      setForm({
        revenue_inr: t.revenue_inr ?? "",
        disbursement_inr: t.disbursement_inr ?? "",
        logins_count: t.logins_count ?? "",
        sanctions_count: t.sanctions_count ?? "",
        override_multiplier: t.override_multiplier ?? "",
        override_reason: t.override_reason ?? "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadAch(); }, [selectedId, period]);

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const body = { user_id: selectedId, period };
      for (const k of ["revenue_inr", "disbursement_inr", "logins_count", "sanctions_count", "override_multiplier", "override_reason"]) {
        const v = form[k];
        if (v !== "" && v != null) body[k] = ["logins_count", "sanctions_count"].includes(k) ? Number(v) : (k === "override_reason" ? String(v) : Number(v));
      }
      await api.post("/admin/targets", body);
      toast.success("Target saved");
      await loadAch();
      const t = await api.get("/admin/targets", { params: { period } });
      setTargets(t.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const selectedUser = useMemo(() => users.find((u) => u.user_id === selectedId), [users, selectedId]);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <DashboardShell title="Employee Targets & Incentives" subtitle="Admin · Debt CRM · Monthly targets & multipliers">
      <Toaster richColors position="top-right" />
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4" data-testid="admin-employee-targets">
        {/* Left: user + period picker */}
        <aside className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-heading text-ink-muted uppercase tracking-wider">Period</label>
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              data-testid="targets-period"
              className="text-[12px] font-mono border border-ink/15 rounded px-2 py-1 bg-white"
            />
            <button onClick={load} className="text-[10px] text-navy border border-navy/30 rounded px-2 py-1 inline-flex items-center gap-1" data-testid="targets-refresh">
              <RefreshCw size={10} /> Refresh
            </button>
          </div>
          <div className="border border-ink/10 rounded-lg bg-white max-h-[540px] overflow-auto">
            <div className="px-3 py-2 border-b border-ink/10 text-[11px] font-heading text-ink-muted uppercase">
              Team ({users.length})
            </div>
            {loading ? (
              <div className="p-3 text-xs italic text-ink-muted">Loading…</div>
            ) : users.length === 0 ? (
              <div className="p-3 text-xs italic text-ink-muted">No team users configured.</div>
            ) : (
              users.map((u) => {
                const t = targets.find((x) => x.user_id === u.user_id);
                const has = t ? true : false;
                return (
                  <button
                    key={u.user_id}
                    onClick={() => setSelectedId(u.user_id)}
                    data-testid={`target-user-${u.user_id}`}
                    className={`w-full text-left px-3 py-2 border-b border-ink/5 text-[12px] flex items-center justify-between hover:bg-navy/5 ${
                      selectedId === u.user_id ? "bg-navy/10 border-l-2 border-l-navy" : ""
                    }`}
                  >
                    <div>
                      <div className="text-navy font-heading">{u.name || u.email}</div>
                      <div className="text-[10px] text-ink-muted font-mono">{u.role}</div>
                    </div>
                    {has ? (
                      <span className="text-[9px] bg-navy/10 text-navy px-1.5 rounded-full">SET</span>
                    ) : (
                      <span className="text-[9px] text-ink-muted">—</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Right: form + achievement */}
        <section className="space-y-4">
          {!selectedUser ? (
            <div className="p-8 border border-dashed border-ink/15 rounded-lg text-center text-sm italic text-ink-muted">
              Select a user to set / view targets.
            </div>
          ) : (
            <>
              <div className="border border-ink/10 rounded-lg bg-white p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Target size={14} className="text-navy" />
                  <h3 className="text-sm font-heading text-navy">
                    Target · {selectedUser.name || selectedUser.email} · <span className="font-mono">{period}</span>
                  </h3>
                </div>

                {ach?.target?.default_multiplier != null && (
                  <div className="text-[11px] text-ink-muted mb-3">
                    Default multiplier: <b className="text-navy">{ach.target.default_multiplier}×</b> ·
                    Baseline revenue: <b>{inrFmt(ach.target.baseline_revenue_inr)}</b> ·
                    <span className="italic ml-1">(2× first 90 days of joining, 3× thereafter)</span>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Revenue target (₹)" value={form.revenue_inr} onChange={(v) => setF("revenue_inr", v)} testId="tgt-revenue" />
                  <Field label="Disbursement target (₹)" value={form.disbursement_inr} onChange={(v) => setF("disbursement_inr", v)} testId="tgt-disb" />
                  <Field label="Logins target (#)" value={form.logins_count} onChange={(v) => setF("logins_count", v)} testId="tgt-logins" />
                  <Field label="Sanctions target (#)" value={form.sanctions_count} onChange={(v) => setF("sanctions_count", v)} testId="tgt-sanctions" />
                  <Field label="Override multiplier (×)" value={form.override_multiplier} onChange={(v) => setF("override_multiplier", v)} testId="tgt-mult" placeholder="leave blank to use default" />
                  <Field label="Override reason" value={form.override_reason} onChange={(v) => setF("override_reason", v)} testId="tgt-reason" placeholder="Required when overriding" text />
                </div>

                <div className="mt-3 flex justify-end">
                  <button
                    onClick={save}
                    disabled={saving}
                    className="text-[12px] font-heading text-navy bg-yellow-400 hover:bg-yellow-500 disabled:opacity-40 rounded px-3 py-1.5 inline-flex items-center gap-1"
                    data-testid="target-save"
                  >
                    <Save size={12} /> {saving ? "Saving…" : "Save target"}
                  </button>
                </div>
              </div>

              {/* Achievement snapshot */}
              {ach && (
                <div className="border border-ink/10 rounded-lg bg-white p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp size={14} className="text-navy" />
                    <h3 className="text-sm font-heading text-navy">Live achievement · {period}</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <AchStat label="Cases" value={ach.actuals?.case_count} pct={null} />
                    <AchStat label="Disbursed" value={inrFmt(ach.actuals?.disbursement_inr)} pct={ach.achievement_pct?.disbursement} />
                    <AchStat label="Sanctions" value={ach.actuals?.sanctions_count} pct={ach.achievement_pct?.sanctions} />
                    <AchStat label="Logins" value={ach.actuals?.logins_count} pct={ach.achievement_pct?.logins} />
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </DashboardShell>
  );
}

function Field({ label, value, onChange, testId, placeholder, text }) {
  return (
    <label className="block">
      <div className="text-[10px] font-heading text-ink-muted uppercase tracking-wider mb-1">{label}</div>
      <input
        type={text ? "text" : "number"}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        placeholder={placeholder || ""}
        className="w-full text-[12px] font-mono border border-ink/15 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-navy"
      />
    </label>
  );
}

function AchStat({ label, value, pct }) {
  return (
    <div className="border border-ink/10 rounded p-3 bg-navy/[0.02]">
      <div className="text-[10px] font-heading text-ink-muted uppercase tracking-wider mb-1">{label}</div>
      <div className="text-sm font-heading text-navy">{value ?? "—"}</div>
      {pct != null && (
        <div className={`text-[10px] mt-1 ${pct >= 100 ? "text-emerald-600" : pct >= 60 ? "text-amber-600" : pct > 0 ? "text-red-600" : "text-ink-muted"}`}>
          {pct}% of target
        </div>
      )}
    </div>
  );
}
